import { useGetExamEventSessionByIdQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import {
  type ExamEventSessionDetailsProviderProps,
  type ExamEventSessionDetailsContextValue,
} from './types';

const ExamEventSessionDetailsContext = createContext<
  ExamEventSessionDetailsContextValue | undefined
>(undefined);

export default function ExamEventSessionDetailsProvider(
  props: Readonly<ExamEventSessionDetailsProviderProps>,
) {
  const { examEventSessionId, examCenterCode, examEventId, children } = props;

  const { data: getExamEventSessionByIdResult, loading: isUseGetExamEventSessionByIdLoading } =
    useGetExamEventSessionByIdQuery({
      variables: {
        getExamEventSessionByIdInput: {
          id: examEventSessionId,
        },
      },
    });

  const examEventSession =
    getExamEventSessionByIdResult?.ecGetExamEventSessionById?.examEventSession;

  const contextValue: ExamEventSessionDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      examEventSession: examEventSession!,
    };
  }, [examEventSession]);

  if (isUseGetExamEventSessionByIdLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (!examEventSession) {
    return (
      <Navigate
        to='/:examCenterCode/proctoring/exam-events/:examEventId/details'
        params={{ examCenterCode, examEventId }}
      />
    );
  }

  return (
    <ExamEventSessionDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </ExamEventSessionDetailsContext.Provider>
  );
}

export { ExamEventSessionDetailsContext };
